import React, { useState } from 'react';
import TrendPanel from './TrendPanel';
import {
  formatNumber,
  formatPercent,
  riskFromZone,
  toneFromOhlsonBand,
  cadenceFromZone,
  latestDirection
} from './primitives';

/**
 * Supporting evidence, tabbed: Trend (chart-first), Scores & Ratios,
 * Monitoring (what to watch), and Actions (prioritized).
 * Watch levels shown are either reported by the scoring engine
 * (Altman thresholds) or accounting identities — never invented.
 */
const EvidenceTabs = ({ company }) => {
  const { ratios, analysis } = company;
  const { distressScore, ohlsonScore, distressAgreement, trend, aiAnalysis, notComputable } =
    analysis;

  const mitigations = aiAnalysis.mitigations || {};
  const nextSteps = mitigations.nextSteps || [];
  const supportGroups = [
    { key: 'liquidity', title: 'Liquidity', items: mitigations.liquidity || [] },
    { key: 'leverage', title: 'Leverage', items: mitigations.leverage || [] },
    { key: 'earningsOperations', title: 'Earnings & operations', items: mitigations.earningsOperations || [] }
  ].filter((group) => group.items.length > 0);
  const diligenceQuestions = aiAnalysis.diligenceQuestions || [];
  const hasActions = nextSteps.length > 0 || supportGroups.length > 0 || diligenceQuestions.length > 0;

  const show = (value, reasonKey, kind = 'ratio') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      const reason = reasonKey ? notComputable?.[reasonKey] : null;
      return reason ? `Not computable (${reason})` : 'Not computable';
    }
    if (kind === 'percent') return formatPercent(value);
    if (kind === 'multiple') return `${Number(value).toFixed(1)}\u00d7`;
    return formatNumber(value);
  };

  const tabs = [
    { id: 'trend', label: 'Trend' },
    { id: 'ratios', label: 'Scores & Ratios' },
    { id: 'monitoring', label: 'Monitoring' },
    ...(hasActions ? [{ id: 'actions', label: 'Actions' }] : [])
  ];

  const [activeTab, setActiveTab] = useState('trend');
  const risk = riskFromZone(distressScore.riskZone);
  const periods = trend && trend.available ? trend.periods : [];

  const directionCell = (dir) => {
    if (!dir) return <span>{'\u2014'}</span>;
    return (
      <span className={`fr-trend-arrow fr-text-${dir.tone}`} aria-hidden="true">
        {dir.glyph}
      </span>
    );
  };

  const monitoringRows = [
    {
      metric: 'Z-Score',
      latest: show(distressScore.altmanZScore, 'distress.altmanZScore'),
      dir: latestDirection(periods, 'zScore', true),
      watch: distressScore.thresholds
        ? `Watch below ${distressScore.thresholds.safe}; escalate below ${distressScore.thresholds.distress}`
        : 'Watch on decline'
    },
    {
      metric: 'Current ratio',
      latest: show(ratios.liquidity.currentRatio, 'liquidity.currentRatio'),
      dir: latestDirection(periods, 'currentRatio', true),
      watch: 'Below 1.00, current liabilities exceed current assets'
    },
    {
      metric: 'Debt / Equity',
      latest: show(ratios.leverage.debtToEquity, 'leverage.debtToEquity'),
      dir: latestDirection(periods, 'debtToEquity', false),
      watch: 'Sustained rise versus prior years'
    },
    {
      metric: 'Net margin',
      latest: formatPercent(ratios.profitability.netMargin),
      dir: latestDirection(periods, 'netMargin', true),
      watch: 'Decline versus prior year'
    }
  ];

  return (
    <div>
      <div className="fr-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'trend' && (
        <TrendPanel
          trend={trend}
          thresholds={trend?.thresholds || distressScore.thresholds}
          riskZone={distressScore.riskZone}
        />
      )}

      {activeTab === 'ratios' && (
        <div>
          {distressScore.equityBasis === 'market' && trend?.available && trend.latestComparableZ != null ? (
            <div className="fr-z-dual" role="note">
              <p className="fr-z-dual-body">
                Headline Z <strong>{show(distressScore.altmanZScore, 'distress.altmanZScore')}</strong> (market equity) can differ from
                the trend series latest year <strong>{show(trend.latestComparableZ)}</strong> (book equity):
                absolute risk vs history, not a data error.
              </p>
            </div>
          ) : null}
          <div className="fr-ratio-grid">
            <div className="fr-ratio-group">
              <h5>Distress scores</h5>
              <div className="fr-ratio-row">
                <span>Altman Z-Score</span>
                <span className={`fr-num fr-text-${risk.tone}`}>
                  {show(distressScore.altmanZScore, 'distress.altmanZScore')}
                </span>
              </div>
              {trend?.available && trend.latestComparableZ != null && distressScore.equityBasis === 'market' ? (
                <div className="fr-ratio-row">
                  <span>Trend Z (latest year)</span>
                  <span className="fr-num">{show(trend.latestComparableZ)}</span>
                </div>
              ) : null}
              {ohlsonScore && ohlsonScore.oScore !== null && (
                <div className="fr-ratio-row">
                  <span>Distress probability</span>
                  <span className={`fr-num fr-text-${toneFromOhlsonBand(ohlsonScore.band)}`}>
                    {formatPercent(ohlsonScore.probabilityOfDistress)}
                  </span>
                </div>
              )}
              {distressAgreement && (
                <div className="fr-ratio-row">
                  <span>Model agreement</span>
                  <span>{distressAgreement.status}</span>
                </div>
              )}
            </div>

            {distressScore.components &&
            (distressScore.components.workingCapitalToAssets != null ||
              distressScore.components.equityToLiabilities != null) ? (
              <div className="fr-ratio-group">
                <h5>Z-Score components</h5>
                <div className="fr-ratio-row">
                  <span>A · WC / Assets</span>
                  <span className="fr-num">
                    {formatPercent(distressScore.components.workingCapitalToAssets)}
                  </span>
                </div>
                <div className="fr-ratio-row">
                  <span>B · RE / Assets</span>
                  <span className="fr-num">
                    {formatPercent(distressScore.components.retainedEarningsToAssets)}
                  </span>
                </div>
                <div className="fr-ratio-row">
                  <span>C · EBIT / Assets</span>
                  <span className="fr-num">{formatPercent(distressScore.components.ebitToAssets)}</span>
                </div>
                <div className="fr-ratio-row">
                  <span>
                    {distressScore.equityBasis === 'market'
                      ? 'D · Market equity / Liabilities'
                      : 'D · Book equity / Liabilities'}
                  </span>
                  <span className="fr-num">{formatNumber(distressScore.components.equityToLiabilities)}</span>
                </div>
                {distressScore.components.salesToAssets != null ? (
                  <div className="fr-ratio-row">
                    <span>E · Sales / Assets</span>
                    <span className="fr-num">{formatNumber(distressScore.components.salesToAssets)}</span>
                  </div>
                ) : null}
                <p className="fr-industry-note" style={{ marginTop: '0.5rem' }}>
                  Audit values for {distressScore.formulaLabel || 'the applied Altman variant'}. A–C shown as
                  percentages of assets.
                  {distressScore.equityBasis === 'market'
                    ? ' D uses market capitalization, not balance-sheet equity.'
                    : ' D uses book shareholders’ equity.'}
                </p>
              </div>
            ) : null}

            <div className="fr-ratio-group">
              <h5>Liquidity</h5>
              <div className="fr-ratio-row">
                <span>Current ratio</span>
                <span className="fr-num">{show(ratios.liquidity.currentRatio, 'liquidity.currentRatio')}</span>
              </div>
              <div className="fr-ratio-row">
                <span>Quick ratio</span>
                <span className="fr-num">{show(ratios.liquidity.quickRatio, 'liquidity.quickRatio')}</span>
              </div>
            </div>

            <div className="fr-ratio-group">
              <h5>Leverage</h5>
              <div className="fr-ratio-row">
                <span>Debt-to-equity</span>
                <span className="fr-num">{show(ratios.leverage.debtToEquity, 'leverage.debtToEquity')}</span>
              </div>
              <div className="fr-ratio-row">
                <span>Interest coverage</span>
                <span className="fr-num">
                  {show(ratios.leverage.interestCoverage, 'leverage.interestCoverage', 'multiple')}
                </span>
              </div>
            </div>

            <div className="fr-ratio-group">
              <h5>Profitability</h5>
              <div className="fr-ratio-row">
                <span>Return on equity</span>
                <span className="fr-num">
                  {show(ratios.profitability.returnOnEquity, 'profitability.returnOnEquity', 'percent')}
                </span>
              </div>
              <div className="fr-ratio-row">
                <span>Return on assets</span>
                <span className="fr-num">
                  {show(ratios.profitability.returnOnAssets, 'profitability.returnOnAssets', 'percent')}
                </span>
              </div>
              <div className="fr-ratio-row">
                <span>Net margin</span>
                <span className="fr-num">
                  {show(ratios.profitability.netMargin, 'profitability.netMargin', 'percent')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'monitoring' && (
        <div>
          <p className="fr-monitor-cadence">
            Suggested review cadence: <strong>{cadenceFromZone(distressScore.riskZone, aiAnalysis)}</strong>{' '}
            (based on holistic financial health).
          </p>
          <div className="fr-table-wrap">
            <table className="fr-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th className="fr-cell-num">Latest</th>
                  <th>Direction</th>
                  <th>Watch level</th>
                </tr>
              </thead>
              <tbody>
                {monitoringRows.map((row) => (
                  <tr key={row.metric}>
                    <td className="fr-cell-strong">{row.metric}</td>
                    <td className="fr-cell-num fr-cell-strong">{row.latest}</td>
                    <td>{directionCell(row.dir)}</td>
                    <td>{row.watch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {periods.length < 2 && (
            <p className="fr-method-note">
              Direction indicators require at least two reported periods.
            </p>
          )}
        </div>
      )}

      {activeTab === 'actions' && hasActions && (
        <div>
          {nextSteps.length > 0 && (
            <div style={{ marginBottom: supportGroups.length > 0 ? '1rem' : 0 }}>
              {nextSteps.map((step, index) => (
                <div key={index} className="fr-priority">
                  <span className="fr-priority-badge">Priority {index + 1}</span>
                  <span className="fr-priority-text">{step}</span>
                </div>
              ))}
            </div>
          )}

          {supportGroups.length > 0 && (
            <div className="fr-actions-grid">
              {supportGroups.map((group) => (
                <div key={group.key} className="fr-actions-group">
                  <h5>{group.title}</h5>
                  <ul>
                    {group.items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {diligenceQuestions.length > 0 && (
            <div className="fr-actions-group">
              <h5>Diligence questions</h5>
              <ul>
                {diligenceQuestions.map((question, index) => (
                  <li key={index} className="fr-num">
                    {index + 1}. {question}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EvidenceTabs;
